class VideoResourcesCache {
  constructor() {
    this.dbName = 'MaestroCache';
    this.version = 2;
    this.videoStoreName = 'videoResources';
    this.lessonStoreName = 'lessonPlans';
    this.db = null;
  }

  // Initialize IndexedDB
  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => {
        console.error('Error opening IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('📦 IndexedDB initialized for video caching');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Create video resources object store if it doesn't exist
        if (!db.objectStoreNames.contains(this.videoStoreName)) {
          const videoStore = db.createObjectStore(this.videoStoreName, { keyPath: 'cacheKey' });
          videoStore.createIndex('timestamp', 'timestamp', { unique: false });
          console.log('📦 Created video resources object store');
        }
        
        // Create lesson plans object store if it doesn't exist
        if (!db.objectStoreNames.contains(this.lessonStoreName)) {
          const lessonStore = db.createObjectStore(this.lessonStoreName, { keyPath: 'cacheKey' });
          lessonStore.createIndex('timestamp', 'timestamp', { unique: false });
          console.log('📦 Created lesson plans object store');
        }
      };
    });
  }

  // Generate cache key from lesson content
  generateCacheKey(lessonContent) {
    if (!lessonContent) return null;
    
    // Create a simple hash from lesson content
    let hash = 0;
    const str = lessonContent.trim();
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return `lesson_${Math.abs(hash)}`;
  }

  // Store video resources in cache
  async storeVideos(unitDay, videos, keywords) {
    try {
      if (!this.db) await this.init();
      
      const cacheKey = `unit_day_${unitDay}`;

      const data = {
        cacheKey,
        videos,
        keywords,
        timestamp: Date.now(),
        unitDay: unitDay
      };

      const transaction = this.db.transaction([this.videoStoreName], 'readwrite');
      const store = transaction.objectStore(this.videoStoreName);
      
      await new Promise((resolve, reject) => {
        const request = store.put(data);
        request.onsuccess = () => {
          console.log(`💾 Cached ${videos.length} videos for lesson`);
          resolve();
        };
        request.onerror = () => reject(request.error);
      });

      return true;
    } catch (error) {
      console.error('Error storing videos in cache:', error);
      return false;
    }
  }

  // Retrieve video resources from cache
  async getVideos(unitDay) {
    try {
      if (!this.db) await this.init();
      
      const cacheKey = `unit_day_${unitDay}`;

      const transaction = this.db.transaction([this.videoStoreName], 'readonly');
      const store = transaction.objectStore(this.videoStoreName);

      return new Promise((resolve, reject) => {
        const request = store.get(cacheKey);
        
        request.onsuccess = () => {
          const result = request.result;
          
          if (result) {
            // Check if cache is still fresh (24 hours)
            const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
            const isExpired = (Date.now() - result.timestamp) > maxAge;
            
            if (!isExpired) {
              console.log(`📦 Retrieved ${result.videos.length} cached videos for lesson`);
              resolve({
                videos: result.videos,
                keywords: result.keywords,
                fromCache: true
              });
            } else {
              console.log('🕒 Cached videos expired, will fetch fresh data');
              // Remove expired entry
              this.removeExpiredEntry(cacheKey);
              resolve(null);
            }
          } else {
            console.log('📦 No cached videos found for lesson');
            resolve(null);
          }
        };
        
        request.onerror = () => {
          console.error('Error retrieving cached videos:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error getting videos from cache:', error);
      return null;
    }
  }

  // Remove expired cache entry
  async removeExpiredEntry(cacheKey) {
    try {
      if (!this.db) return;
      
      const transaction = this.db.transaction([this.videoStoreName], 'readwrite');
      const store = transaction.objectStore(this.videoStoreName);
      store.delete(cacheKey);
    } catch (error) {
      console.error('Error removing expired cache entry:', error);
    }
  }

  // Clear cached videos for a specific unit day
  async clearUnitDayCache(unitDay) {
    try {
      if (!this.db) await this.init();
      
      const cacheKey = `unit_day_${unitDay}`;
      const transaction = this.db.transaction([this.videoStoreName], 'readwrite');
      const store = transaction.objectStore(this.videoStoreName);
      
      await new Promise((resolve, reject) => {
        const request = store.delete(cacheKey);
        request.onsuccess = () => {
          console.log(`🗑️ Cleared cached videos for Unit Day ${unitDay}`);
          resolve();
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error clearing unit day cache:', error);
    }
  }

  // Clear all cached videos (useful for debugging)
  async clearCache() {
    try {
      if (!this.db) await this.init();
      
      const transaction = this.db.transaction([this.videoStoreName], 'readwrite');
      const store = transaction.objectStore(this.videoStoreName);
      
      await new Promise((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => {
          console.log('🗑️ Cleared all cached videos');
          resolve();
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }

  // Get cache size for debugging
  async getCacheSize() {
    try {
      if (!this.db) await this.init();
      
      const transaction = this.db.transaction([this.videoStoreName], 'readonly');
      const store = transaction.objectStore(this.videoStoreName);
      
      return new Promise((resolve, reject) => {
        const request = store.count();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error getting cache size:', error);
      return 0;
    }
  }
}

// Export singleton instance
export const videoResourcesCache = new VideoResourcesCache();
export default VideoResourcesCache; 